import { ChevronDown } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@nexus/ui";
import { isClientProfile, profileLabel, SELECTABLE_PROFILES } from "@/config/uiConfig";
import type { DashboardRecord } from "@/lib/dashboard/types";
import type { Tenant } from "@/types";
import type { GrantableAccount } from "../useGrantableAccounts";

export function DashboardAudience({
  dashboard,
  accounts,
  tenants,
}: {
  dashboard: DashboardRecord;
  accounts: readonly GrantableAccount[];
  tenants: readonly Tenant[];
}) {
  const accountLabel = (key: string) =>
    accounts.find((account) => account.account_id === key)?.email ?? key;
  const tenantLabel = (key: string) =>
    tenants.find((tenant) => String(tenant.tenant_id) === key)?.tenant_label ?? `tenant ${key}`;
  const owner = dashboard.access?.owned
    ? "You (owner)"
    : dashboard.owner_account_id
      ? `${accountLabel(dashboard.owner_account_id)} (owner)`
      : "Owner";
  const grants = dashboard.state === "draft" ? [] : dashboard.audience_grants;
  if (!grants?.length) {
    return (
      <span className="block w-full max-w-64 truncate text-secondary md:w-64">
        {grants || dashboard.state === "draft" ? "Owner only" : "Access unavailable"}
      </span>
    );
  }

  const priority = (grant: (typeof grants)[number]) => {
    switch (grant.grantee_type) {
      case "tenant":
        return 0;
      case "account":
        return isClientProfile(
          accounts.find((account) => account.account_id === grant.grantee_key)?.profile,
        )
          ? 1
          : 2;
      case "scope":
        return 3;
      case "profile":
        return isClientProfile(grant.grantee_key) ? 1 : 4;
    }
  };
  const internalProfiles = new Set(
    SELECTABLE_PROFILES.filter((profile) => !isClientProfile(profile)),
  );
  const grantedProfiles = new Set(
    grants.filter((grant) => grant.grantee_type === "profile").map((grant) => grant.grantee_key),
  );
  const everyoneAtNagra = [...internalProfiles].every((profile) => grantedProfiles.has(profile));
  const sortedGrants = grants
    .filter(
      (grant) =>
        !everyoneAtNagra ||
        grant.grantee_type !== "profile" ||
        ![...internalProfiles].some((profile) => profile === grant.grantee_key),
    )
    .sort((first, second) => priority(first) - priority(second));
  const recipients = sortedGrants.map((grant) => {
    switch (grant.grantee_type) {
      case "account":
        return { label: accountLabel(grant.grantee_key), summary: accountLabel(grant.grantee_key) };
      case "profile":
        return {
          label: `Every ${profileLabel(grant.grantee_key)}`,
          summary: profileLabel(grant.grantee_key),
        };
      case "tenant":
        return {
          label: `Everyone at ${tenantLabel(grant.grantee_key)}`,
          summary: tenantLabel(grant.grantee_key),
        };
      case "scope":
        return { label: `Scope: ${grant.grantee_key}`, summary: `Scope: ${grant.grantee_key}` };
    }
  });
  if (everyoneAtNagra) recipients.push({ label: "Everyone at NAGRA", summary: "NAGRA" });
  const summary = recipients.map((recipient) => recipient.summary).join(", ");
  const details = [...recipients.map((recipient) => recipient.label), owner];

  return (
    <div
      className="w-full max-w-64 md:w-64"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Who can open ${dashboard.name}`}
            title={details.join("\n")}
            className="flex w-full min-w-0 items-center gap-1.5 rounded-control text-left text-secondary hover:text-primary focus-visible:outline-2 focus-visible:outline-focus"
          >
            <span className="min-w-0 truncate">{summary}</span>
            <ChevronDown className="size-3.5 shrink-0" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          aria-label={`Who can open ${dashboard.name}`}
          className="w-80 max-w-[calc(100vw-2rem)] p-3 text-sm"
        >
          <p className="mb-2 font-medium text-primary">Who can open it</p>
          <ul className="max-h-60 space-y-2 overflow-y-auto text-secondary">
            {details.map((recipient, index) => (
              <li key={index} className="[overflow-wrap:anywhere]">
                {recipient}
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}
